import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import ContainerMaxWidthXl from '@deltek/specification-client/core/Surfaces/ContainerMaxWidthXl'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'

const useStyles = makeStyles((theme: Theme) => ({
    menuContainer: {
        background: theme.palette.grey[700],
        height: 'calc(100vh - 60px)',
        overflowX: 'hidden',
        textAlign: 'center',
        position: 'absolute',
        width: '100%',
    },
    menuListContainer: {
        padding: theme.spacing(2, 3),
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2, 2),
        },
        borderBottom: `3px solid rgba(255, 255, 255, 0.54)`,
    },
}))

const useButtonStyles = makeStyles((theme: Theme) => ({
    activeTabButton: {
        color: theme.palette.primary.dark,
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.common.white,
        '&:hover': {
            backgroundColor: theme.palette.common.white,
        },
    },
    inactiveTabButton: {
        color: theme.palette.common.white,
    },
    menuItemButton: {
        borderRadius: theme.spacing(800),
        textAlign: 'center',
        fontSize: pxToRem(18),
    },
}))

export interface FullNavBarMenuProps {
    customContent?: MenuItemConfig[] | React.ReactNode | null
    helpMenuItems?: MenuItemConfig[]
    userMenuItems?: MenuItemConfig[]
}

export interface MenuItemProps {
    itemConfig: MenuItemConfig
    index: number
}

const MenuItem: React.FC<MenuItemProps> = ({ itemConfig, index }) => {
    const classes = useButtonStyles()
    const { pathname } = useLocation()

    const navLinkProps = {
        component: NavLink,
        to: itemConfig.onClick,
    }

    const nonNavLinkProps = {
        onClick: itemConfig.onClick as VoidFunction,
    }

    return (
        <Button
            data-testid={itemConfig.dataTestId ?? `item-${index}`}
            className={clsx(
                classes.menuItemButton,
                itemConfig?.checkRoute?.(pathname)
                    ? classes.activeTabButton
                    : classes.inactiveTabButton
            )}
            {...(typeof itemConfig.onClick === 'string'
                ? navLinkProps
                : nonNavLinkProps)}
            disableRipple
        >
            {itemConfig.label}
        </Button>
    )
}

const FullNavBarMenu: React.FC<FullNavBarMenuProps> = ({
    customContent,
    helpMenuItems = [],
    userMenuItems = [],
}) => {
    const classes = useStyles()

    const customContentItemGroup = customContent
        ? customContent instanceof Array
            ? [...(customContent as MenuItemConfig[])]
            : [
                  {
                      component:
                          !(customContent instanceof Array) &&
                          customContent !== undefined &&
                          customContent !== null &&
                          customContent,
                  },
              ]
        : []

    const items: MenuItemConfig[] = [
        {
            label: 'Map',
            onClick: '/dashboard',
            checkRoute: (currentRoute: string) =>
                currentRoute.startsWith('/dashboard'),
        },
        {
            label: 'Projects',
            onClick: '/projects/home',
            checkRoute: (currentRoute: string) =>
                currentRoute.startsWith('/projects'),
        },
    ]

    const itemGroups: MenuItemConfig[][] = [
        items,
        customContentItemGroup,
        helpMenuItems,
        userMenuItems,
    ].filter((g) => g.length)

    return (
        <div
            data-testid="fullNavMenu"
            className={clsx('menuContainer', classes.menuContainer)}
        >
            <ContainerMaxWidthXl>
                <GridContainer direction="column">
                    {itemGroups.map(
                        (itemGroup: MenuItemConfig[], groupIndex: number) => {
                            return (
                                <GridItem
                                    container
                                    direction="column"
                                    spacing={1}
                                    key={groupIndex}
                                    className={clsx(
                                        'menuGroup',
                                        classes.menuListContainer
                                    )}
                                >
                                    {itemGroup.map(
                                        (
                                            menuItem: MenuItemConfig,
                                            index: number
                                        ) => {
                                            return (
                                                <GridItem
                                                    key={`${menuItem.label}-${index}`}
                                                >
                                                    {menuItem.component ? (
                                                        menuItem.component
                                                    ) : (
                                                        <MenuItem
                                                            itemConfig={
                                                                menuItem
                                                            }
                                                            index={index}
                                                        />
                                                    )}
                                                </GridItem>
                                            )
                                        }
                                    )}
                                </GridItem>
                            )
                        }
                    )}
                </GridContainer>
            </ContainerMaxWidthXl>
        </div>
    )
}

export default FullNavBarMenu
